import React, { useState } from 'react';
import { Check, FolderOpen, FolderX, Loader2, Pencil, Share2, Trash2, X } from 'lucide-react';
import type { Meeting } from '../../types';
import type { MeetingFolderDTO } from '../../global';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { ShareMeetingDialog } from '../meetings/ShareMeetingDialog';
import { buttonRowProps, meetingDragProps } from './dragMeeting';
import { t } from '../../lib/i18n';

interface FolderViewProps {
  folder: MeetingFolderDTO;
  meetings: Meeting[];
  onSelectMeeting: (meeting: Meeting) => void;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onRemoveMeeting: (meeting: Meeting) => void;
  onError: (message: string) => void;
}

/** Klasörün toplantı listesi: buradan açılır, paylaşılır, klasörden çıkarılır. */
export const FolderView: React.FC<FolderViewProps> = ({ folder, meetings, onSelectMeeting, onRename, onDelete, onRemoveMeeting, onError }) => {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(folder.name);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const share = window.zabit.folders?.share;

  const saveName = async (event: React.FormEvent) => {
    event.preventDefault();
    const next = name.trim();
    if (!next || next === folder.name) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    try {
      await onRename(next);
      setRenaming(false);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await onDelete();
    } catch (err) {
      onError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto w-full py-4 px-1">
      <div className="flex items-center gap-3 mb-1 min-h-10">
        <FolderOpen className="w-6 h-6 text-[#46CBE2] shrink-0" />
        {renaming ? (
          <form onSubmit={(event) => void saveName(event)} className="flex items-center gap-1.5 flex-1 min-w-0">
            <Input
              autoFocus
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setRenaming(false);
              }}
              aria-label={t('Klasör adı', 'Folder name')}
            />
            <Button type="submit" size="icon" variant="ghost" disabled={busy} aria-label={t('Kaydet', 'Save')}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            </Button>
            <Button type="button" size="icon" variant="ghost" onClick={() => setRenaming(false)} aria-label={t('Vazgeç', 'Cancel')}>
              <X className="size-4" />
            </Button>
          </form>
        ) : (
          <>
            <h1 className="text-[26px] font-bold tracking-tight truncate flex-1 min-w-0">{folder.name}</h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setName(folder.name);
                setRenaming(true);
              }}
              title={t('Adını değiştir', 'Rename')}
              aria-label={t('Adını değiştir', 'Rename')}
            >
              <Pencil className="size-4" />
            </Button>
            {share && (
              <Button variant="outline" onClick={() => setIsShareOpen(true)}>
                <Share2 className="size-4" />
                {t('Paylaş', 'Share')}
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => setIsDeleteOpen(true)} title={t('Klasörü sil', 'Delete folder')} aria-label={t('Klasörü sil', 'Delete folder')}>
              <Trash2 className="size-4" />
            </Button>
          </>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-6">{t(`${meetings.length} toplantı`, `${meetings.length} meeting${meetings.length === 1 ? '' : 's'}`)}</p>

      {meetings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
          {t('Bu klasör boş. Soldaki listeden toplantıları sürükleyip klasörün üzerine bırak.', 'This folder is empty. Drag meetings from the list on the left and drop them on the folder.')}
        </div>
      ) : (
        <ul className="divide-y divide-border border-y border-border">
          {meetings.map((meeting) => (
            <li key={meeting.id} {...meetingDragProps(meeting.id)} className="group flex items-center gap-2 hover:bg-accent/50 transition-colors">
              <div {...buttonRowProps(() => onSelectMeeting(meeting))} className="flex-1 min-w-0 text-left px-2 py-3 cursor-pointer">
                <span className="block truncate text-[15px] font-medium">{meeting.title}</span>
                <span className="block text-sm text-muted-foreground">{[meeting.date, meeting.duration].filter(Boolean).join(' · ')}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 shrink-0"
                onClick={() => onRemoveMeeting(meeting)}
                title={t('Klasörden çıkar', 'Remove from folder')}
                aria-label={t(`${meeting.title} toplantısını klasörden çıkar`, `Remove ${meeting.title} from the folder`)}
              >
                <FolderX className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {share && <ShareMeetingDialog open={isShareOpen} api={share} targetId={folder.id} title={folder.name} kind="folder" onClose={() => setIsShareOpen(false)} />}

      <Dialog
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title={t('Klasör silinsin mi?', 'Delete this folder?')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsDeleteOpen(false)}>
              {t('Vazgeç', 'Cancel')}
            </Button>
            <Button onClick={() => void confirmDelete()} disabled={busy} className="bg-rose-600 text-white hover:bg-rose-500">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              {t('Klasörü sil', 'Delete folder')}
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t(
            `“${folder.name}” silinecek. İçindeki toplantılar silinmez, klasörsüz kalır. Klasörün paylaşım bağlantısı çalışmaz olur.`,
            `“${folder.name}” will be deleted. Its meetings stay, without a folder. The folder’s share link stops working.`
          )}
        </p>
      </Dialog>
    </div>
  );
};
